import {
  Carousel,
  CarouselCard,
  CarouselIndexChangeData,
  CarouselNav,
  CarouselNavButton,
  CarouselNavContainer,
  CarouselSlider,
  CarouselViewport,
  Spinner,
} from '@fluentui/react-components';
import { useData } from '@microsoft/teamsfx-react';
import { observer } from 'mobx-react-lite';
import { SyntheticEvent, useMemo, useState } from 'react';
import { isMobile } from 'react-device-detect';

import ChallengeSpotlight from 'src/components/molecules/challenge-spotlight/ChallengeSpotlight';
import DailyHabitsSpotlight from 'src/components/molecules/daily-habits-spotlight/DailyHabitsSpotlight';
import RightSideImageSpotlight from 'src/components/molecules/right-side-image-spotlight/RightSideImageSpotlight';
import RightSideVideoSpotlight from 'src/components/molecules/right-side-video-spotlight/RightSideVideoSpotlight';
import WidgetContainer from 'src/components/molecules/widget-container/WidgetContainer';
import { useStore } from 'src/hooks/useStore';
import { CardData } from 'src/models/CardActivities/CardData';
import { CardTemplate } from 'src/utils/enums';
import { useCardActivitiesCarouselStyles } from './CardActivitiesCarousel.styles';

const CardActivitiesCarousel: React.FC = () => {
  const { activitiesStore } = useStore();
  const {
    loading: isCardActivitiesLoading,
    error: loadCardActivitiesError,
    data: cardActivities,
  } = useData(activitiesStore.loadCardActivities);
  const styles = useCardActivitiesCarouselStyles();
  const [activeIndex, setActiveIndex] = useState(0);
  const spotlight = cardActivities?.spotlight;

  const generateSpotlight = useMemo(() => {
    const getSpotlightTemplate = (card: CardData, index: number, spotlightLength: number, activeIdx: number): JSX.Element => {
      const cardAnnouncement = `${index + 1} of ${spotlightLength}`;
      let content;

      switch (card.template) {
        case CardTemplate.DAILY_HABITS: {
          content = <DailyHabitsSpotlight card={card} />;
          break;
        }
        case CardTemplate.RIGHT_SIDE_VIDEO: {
          content = <RightSideVideoSpotlight card={card} />;
          break;
        }
        case CardTemplate.STEP_BATTLE:
        case CardTemplate.CHALLENGES: {
          content = <ChallengeSpotlight card={card} />;
          break;
        }
        case CardTemplate.HALF_IMAGE:
        case CardTemplate.RIGHT_SIDE_IMAGE:
        default: {
          content = <RightSideImageSpotlight card={card} />;
          break;
        }
      }

      const cardContent = index === activeIdx && content;
      return (
        <CarouselCard
          className={styles.carouselCard}
          aria-label={cardAnnouncement}
          id={card.id}
          key={card.id}
          data-testid="carousel-card"
        >
          {cardContent}
        </CarouselCard>
      );
    };

    return spotlight?.map((card: CardData, index: number) => getSpotlightTemplate(card, index, spotlight.length, activeIndex)) ?? [];
  }, [activeIndex, spotlight]);

  const generateCarousel = useMemo(() => {
    const autoplayAriaLabel = { 'aria-label': 'Enable autoplay' };
    const nextAriaLabel = { 'aria-label': 'go to next' };
    const prevAriaLabel = { 'aria-label': 'go to prev' };
    const carouselButtonFunction = (index: number): JSX.Element => (
      <CarouselNavButton aria-label={`Carousel Nav Button ${index}`} data-testid={`carousel-nav-button-${index}`} />
    );

    const activeIndexChangeHandler: (ev: SyntheticEvent | Event, data: CarouselIndexChangeData) => void = (e, data) => setActiveIndex(data.index);

    return (
      <Carousel className={styles.carouselContainer} groupSize={1} circular onActiveIndexChange={activeIndexChangeHandler}>
        <CarouselViewport>
          <CarouselSlider>{generateSpotlight}</CarouselSlider>
        </CarouselViewport>

        <CarouselNavContainer className={styles.bottomCarouselChild} layout="inline" autoplay={autoplayAriaLabel} next={nextAriaLabel} prev={prevAriaLabel}>
          {!isMobile && <CarouselNav>{carouselButtonFunction}</CarouselNav>}
        </CarouselNavContainer>
      </Carousel>
    );
  }, [generateSpotlight]);

  if (loadCardActivitiesError) {
    throw loadCardActivitiesError;
  }

  if (isCardActivitiesLoading) return <Spinner />;

  if (!cardActivities || !cardActivities.spotlight || cardActivities.spotlight.length === 0) return null;

  return <WidgetContainer title="">{generateCarousel}</WidgetContainer>;
};

export default observer(CardActivitiesCarousel);
